import { Users, TrendingDown } from 'lucide-react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  LabelList,
} from 'recharts';
import type { FunnelResult } from '../../../../api/widgetTelemetry';
import StatCard from '../shared/StatCard';

interface FunnelOverviewProps {
  data: FunnelResult | null;
  loading: boolean;
}

const STAGE_LABELS: Record<string, string> = {
  widget_loaded: 'Widget Loaded',
  geolocation_resolved: 'Geo Resolved',
  venue_viewed: 'Venue Viewed',
  workshop_selected: 'Workshop Selected',
  begin_checkout: 'Begin Checkout',
  purchase: 'Purchase',
};

export default function FunnelOverview({ data, loading }: FunnelOverviewProps) {
  if (loading) {
    return (
      <div className="grid grid-cols-3 gap-4">
        {[1, 2, 3].map((i) => (
          <div
            key={i}
            className="bg-white dark:bg-[#1a1f1c] rounded-lg p-4 border border-stone-200 dark:border-stone-700 animate-pulse h-24"
          />
        ))}
      </div>
    );
  }

  if (!data) return null;

  const first = data.stages[0];
  const last = data.stages[data.stages.length - 1];
  const endToEndRate = first && first.sessions > 0 ? last.sessions / first.sessions : 0;

  // Find biggest drop-off between adjacent stages
  let biggestDrop = { from: '', to: '', rate: 1 };
  for (let i = 1; i < data.stages.length; i++) {
    const prev = data.stages[i - 1];
    const curr = data.stages[i];
    if (curr.stepRate < biggestDrop.rate && prev.sessions > 0) {
      biggestDrop = { from: prev.stage, to: curr.stage, rate: curr.stepRate };
    }
  }

  const chartData = data.stages.map((s) => ({
    stage: STAGE_LABELS[s.stage] ?? s.stage,
    sessions: s.sessions,
    cumulativeRate: Math.round(s.cumulativeRate * 1000) / 10,
    stepRate: Math.round(s.stepRate * 1000) / 10,
  }));

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-3 gap-4">
        <StatCard
          label="Total Sessions"
          value={data.totalSessions.toLocaleString()}
          icon={Users}
          iconColor="text-water-600"
        />
        <StatCard
          label="End-to-End Conversion"
          value={`${(endToEndRate * 100).toFixed(2)}%`}
          icon={TrendingDown}
          iconColor="text-moss-600"
        />
        <StatCard
          label="Biggest Drop-off"
          value={
            biggestDrop.from
              ? `${(biggestDrop.rate * 100).toFixed(0)}% @ ${STAGE_LABELS[biggestDrop.to] ?? biggestDrop.to}`
              : '—'
          }
          icon={TrendingDown}
          iconColor="text-amber-600"
        />
      </div>

      <div className="bg-white dark:bg-[#1a1f1c] rounded-lg border border-stone-200 dark:border-stone-700 p-4">
        <h3 className="text-sm font-medium text-bark-900 dark:text-stone-100 mb-4">
          Funnel — sessions per stage
        </h3>
        {chartData.every((d) => d.sessions === 0) ? (
          <p className="text-xs text-stone-400 text-center py-8">No funnel data</p>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData} margin={{ top: 20, right: 20, left: 10, bottom: 40 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="stage" tick={{ fontSize: 11 }} angle={-15} textAnchor="end" />
              <YAxis tick={{ fontSize: 11 }} />
              {/* eslint-disable-next-line @typescript-eslint/no-explicit-any -- Recharts v3 Tooltip formatter types are overly strict */}
              <Tooltip formatter={((value: number) => value.toLocaleString()) as any} />
              <Bar dataKey="sessions" fill="#4A6741" radius={[4, 4, 0, 0]}>
                <LabelList
                  dataKey="cumulativeRate"
                  position="top"
                  formatter={(v: unknown) => (typeof v === 'number' ? `${v.toFixed(1)}%` : '')}
                  style={{ fontSize: 11, fill: '#6B7280' }}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      <div className="bg-white dark:bg-[#1a1f1c] rounded-lg border border-stone-200 dark:border-stone-700 overflow-x-auto">
        <div className="px-4 py-3 border-b border-stone-200 dark:border-stone-700">
          <h3 className="text-sm font-medium text-bark-900 dark:text-stone-100">Stage Detail</h3>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-stone-50 dark:bg-[#1e2520]">
            <tr>
              <th className="px-4 py-3 text-left font-medium text-stone-600 dark:text-stone-400">Stage</th>
              <th className="px-4 py-3 text-right font-medium text-stone-600 dark:text-stone-400">Sessions</th>
              <th className="px-4 py-3 text-right font-medium text-stone-600 dark:text-stone-400">
                Cumulative %
              </th>
              <th className="px-4 py-3 text-right font-medium text-stone-600 dark:text-stone-400">
                Step %
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-stone-100 dark:divide-stone-800">
            {data.stages.map((row) => (
              <tr key={row.stage} className="hover:bg-moss-50/50 dark:hover:bg-[#242d28]">
                <td className="px-4 py-2 text-bark-900 dark:text-stone-200">
                  {STAGE_LABELS[row.stage] ?? row.stage}
                </td>
                <td className="px-4 py-2 text-right">{row.sessions.toLocaleString()}</td>
                <td className="px-4 py-2 text-right">{(row.cumulativeRate * 100).toFixed(2)}%</td>
                <td className="px-4 py-2 text-right">{(row.stepRate * 100).toFixed(2)}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
